/**
 * Editable starting points for common Australian card earn structures. STARTING POINT ONLY — check your card's current
 * terms; rates, caps, merchant lists and fees change. Each template cites where its figures came from and when they were
 * checked; anything not confirmed on an issuer page is listed in `unverified`. Category names resolve against the
 * entity's own categories when a card is created (`cardFromTemplate`).
 */
import { z } from 'zod';
import { EarnRuleSchema, EarnMatchSchema } from '../types.js';
export const TEMPLATE_DISCLAIMER = "Starting point — check your card's current terms; rates change.";
export const RewardsTemplateSchema = z.object({
    id: z.string().regex(/^[a-z0-9-]+$/),
    name: z.string().min(1),
    issuerLabel: z.string().nullable(),
    annualFeeCents: z.number().int().min(0),
    pointValueCents: z.number().min(0),
    baseRate: z.number().min(0),
    monthlyBonusCapPoints: z.number().int().positive().nullable(),
    monthlySpendCapCents: z.number().int().positive().nullable().optional(),
    rules: z.array(EarnRuleSchema.extend({ match: EarnMatchSchema.extend({ categoryNames: z.array(z.string()).optional() }) })),
    notes: z.string(),
    source: z.object({ url: z.string().url(), checkedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/) }).nullable(),
    /** Fields not confirmed on an issuer page (third-party, assumed or the user's own estimate). */
    unverified: z.array(z.string()),
});
/** Default category/merchant/MCC mappings (shared by the templates; edit freely on the card). */
export const EATS_AND_DRINKS = { categoryNames: ['Dining out', 'Coffee', 'Takeaway & delivery'], mcc: ['5812-5814'] };
export const STREAMING = {
    merchantNames: ['Netflix', 'Stan', 'Binge', 'Disney', 'Spotify', 'Apple Music', 'Apple TV', 'YouTube Premium', 'Paramount', 'Prime Video', 'Kayo', 'Foxtel Now'],
    mcc: ['4899', '5815-5818'],
};
export const SUPERMARKETS = { categoryNames: ['Groceries'], merchantNames: ['Woolworths', 'Coles', 'Aldi', 'IGA'], mcc: ['5411'] };
export const MAJOR_RETAILERS = { merchantNames: ['Amazon', 'Kmart', 'Bunnings', 'Uniqlo', 'Myer', 'The Iconic', 'JB Hi-Fi', 'Harvey Norman'] };
export const FUEL = { categoryNames: ['Fuel'], mcc: ['5541-5542'] };
const CHECKED = '2026-10-19';
export const REWARDS_TEMPLATES = [
    {
        id: 'amex-essential-style', name: 'Amex Essential Rewards-style', issuerLabel: 'American Express',
        annualFeeCents: 108_00, pointValueCents: 0.5, baseRate: 1, monthlyBonusCapPoints: 10_000,
        rules: [
            { label: 'Eats & drinks', rate: 5, match: EATS_AND_DRINKS },
            { label: 'Streaming', rate: 3, match: STREAMING },
            { label: 'Major retailers', rate: 2, match: MAJOR_RETAILERS },
        ],
        notes: 'Bonus rates on the first 10,000 points a month, then 1 point per $1. Bonus spend must be in Australia, direct with the merchant (not bundled or billed by a third party). Fee $9 a month.',
        source: { url: 'https://www.americanexpress.com/en-au/credit-cards/essential-rewards-credit-card/', checkedOn: CHECKED },
        unverified: ['pointValueCents (your own estimate)', 'streaming merchant list (Amex publishes its current list separately)', 'major retailers list (from Amex accelerated-earn page; check it is current)'],
    },
    {
        id: 'amex-explorer-style', name: 'Amex Explorer-style', issuerLabel: 'American Express',
        annualFeeCents: 395_00, pointValueCents: 0.5, baseRate: 2, monthlyBonusCapPoints: null,
        rules: [
            { label: 'Government', rate: 1, match: { merchantNames: ['Australian Taxation Office', 'ATO', 'Australia Post', 'Service NSW', 'VicRoads'], mcc: ['9211-9402'] } },
        ],
        notes: '2 points per $1 on everyday purchases, 1 per $1 with government bodies in Australia. The yearly travel credit is not counted here.',
        source: { url: 'https://www.americanexpress.com/en-au/credit-cards/explorer-credit-card/', checkedOn: CHECKED },
        unverified: ['pointValueCents (your own estimate)', 'government merchant list and MCC range (approximation of "government bodies")'],
    },
    {
        id: 'supermarket-bonus', name: 'Supermarket-bonus card', issuerLabel: null,
        annualFeeCents: 0, pointValueCents: 0.5, baseRate: 1, monthlyBonusCapPoints: null,
        rules: [
            { label: 'Supermarkets', rate: 2, match: SUPERMARKETS },
            { label: 'Fuel', rate: 2, match: FUEL },
        ],
        notes: 'A generic structure: extra points at supermarkets and fuel, 1 point per $1 elsewhere. Enter your own card\'s rates, fee and any cap.',
        source: null,
        unverified: ['all rates', 'annualFeeCents', 'pointValueCents'],
    },
    {
        id: 'coles-rewards-style', name: 'Coles Rewards Mastercard-style', issuerLabel: 'Coles Financial Services',
        annualFeeCents: 99_00, pointValueCents: 0.5, baseRate: 1, monthlyBonusCapPoints: null, monthlySpendCapCents: 8_000_00,
        rules: [{ label: 'Coles Supermarkets', rate: 2, match: { merchantNames: ['Coles'] } }],
        notes: '2 Flybuys points per $1 at Coles Supermarkets, 1 per $1 elsewhere, on the first $8,000 of eligible purchases each statement period (modelled per calendar month); nothing above that. Scanning Flybuys at Coles adds 1 point per $1 (not counted here). 2,000 points = $10 off. Fee $99 a year.',
        source: { url: 'https://www.coles.com.au/credit-cards/rewards', checkedOn: CHECKED },
        unverified: ['pointValueCents (2,000 points = $10, i.e. 0.5¢)', 'cap period (statement period, modelled as a calendar month)', "merchant match ('Coles' also catches Coles Express/online — edit if needed)"],
    },
    {
        id: 'flat-rate', name: 'Flat-rate card', issuerLabel: null,
        annualFeeCents: 0, pointValueCents: 0.5, baseRate: 1, monthlyBonusCapPoints: null, rules: [],
        notes: 'The same points on everything. Set the rate and fee to match your card.', source: null,
        unverified: ['baseRate', 'annualFeeCents', 'pointValueCents'],
    },
    {
        id: 'custom', name: 'Custom card', issuerLabel: null,
        annualFeeCents: 0, pointValueCents: 0.5, baseRate: 1, monthlyBonusCapPoints: null, rules: [],
        notes: '', source: null, unverified: ['everything — you enter the earn rates'],
    },
];
/** A card input from a template: category names become this entity's category ids (names it lacks are dropped). */
export const cardFromTemplate = (t, categories) => {
    const byName = new Map(categories.filter((c) => c.kind === 'expense').map((c) => [c.name.toLowerCase(), c.id]));
    const rules = t.rules.map(({ match: { categoryNames, ...match }, ...r }) => {
        const categoryIds = (categoryNames ?? []).map((n) => byName.get(n.toLowerCase())).filter((x) => Boolean(x));
        return { ...r, match: { ...match, ...(categoryIds.length ? { categoryIds } : {}) } };
    });
    return {
        name: t.name, issuerLabel: t.issuerLabel, annualFeeCents: t.annualFeeCents, pointValueCents: t.pointValueCents, baseRate: t.baseRate,
        monthlyBonusCapPoints: t.monthlyBonusCapPoints, monthlySpendCapCents: t.monthlySpendCapCents ?? null, rules, notes: [t.notes, TEMPLATE_DISCLAIMER].filter(Boolean).join(' '), templateId: t.id,
    };
};
//# sourceMappingURL=templates.js.map